import { m } from 'motion/react';
import { FC } from 'react';
import { CheckIcon } from './CheckIcon';

interface IconProps {
  color?: string;
  width?: number;
  height?: number;
  strokeWidth?: number;
}

interface ListItem {
  title?: string;
  subtitle?: string;
  iconProps?: IconProps;
  text: string;
  boldParts?: string[];
}

export interface TextBoxProps {
  title?: string;
  backgroundColor?: string;
  numberOfFields?: number;
  listItems?: ListItem[];
  titleStyle?: string;
  textSize?: string;
  centerText?: boolean;
  iconOnTop?: boolean;
  paddingClass?: string;
  mtClass?: string;
  mbClass?: string;
  className?: string;
}

export const highlightText = (text: string, boldParts?: string[]) => {
  if (!boldParts || boldParts.length === 0) return text;

  const regex = new RegExp(
    `(${boldParts.map((bp) => bp.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')).join('|')})`,
    'gi',
  );
  return text.split(regex).map((part, idx) =>
    boldParts.includes(part) ? (
      <strong key={idx} className="font-bold">
        {part}
      </strong>
    ) : (
      part
    ),
  );
};

export const TextBox: FC<TextBoxProps> = ({
  title,
  backgroundColor = 'bg-gray-100',
  numberOfFields = 3,
  listItems = [],
  textSize = 'text-base leading-[24px]',
  titleStyle = 'mb-14 text-[40px] max-w-[717px] leading-[48px]',
  centerText = false,
  iconOnTop = false,
  mtClass = 'mt-14',
  mbClass = 'mb-14',
  paddingClass = 'py-8 px-4',
  className = '',
}) => {
  const limitedItems = listItems.slice(0, numberOfFields || 3);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.2 },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.5 } },
  };

  return (
    <div
      className={`flex flex-col items-center ${backgroundColor} ${paddingClass} ${mtClass} ${mbClass}
      ${className}`}
    >
      {title && (
        <h2
          className={`font-normal ${centerText ? 'text-center' : 'text-left'} ${titleStyle} px-3`}
          style={{ hyphens: 'none', overflowWrap: 'normal' }}
        >
          {title}
        </h2>
      )}

      <m.div
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.5 }}
        variants={containerVariants}
        className={`grid grid-cols-1 lg:grid-cols-3 gap-8 w-full max-w-7xl ${iconOnTop ? 'lg:justify-start' : 'justify-items-start md:justify-items-center'}`}
      >
        {limitedItems.map((item, index) => (
          <m.div
            key={index}
            variants={itemVariants}
            className={`flex flex-col ${centerText ? 'items-center text-center' : 'items-start text-left'} gap-y-3  w-full md:max-w-[460px] ${
              index === 1 ? 'self-center' : ''
            } ${iconOnTop ? 'mx-auto lg:mb-auto' : ''}`}
          >
            {item.title && (
              <h2 className="text-xl font-bold leading-tight">{item.title}</h2>
            )}

            <div
              className={`flex gap-x-3 ${centerText ? 'justify-center' : 'items-start'} ${iconOnTop ? 'flex-col items-center' : ''}`}
            >
              {item.iconProps && (
                <div
                  className={`leading-none p-1 sm:p-0 flex-shrink-0 ${iconOnTop ? 'mb-3' : ''}`}
                >
                  <CheckIcon {...item.iconProps} />
                </div>
              )}

              {item.subtitle ? (
                <h3 className={`${textSize}`}>
                  <span className="font-semibold">{item.subtitle}</span>
                </h3>
              ) : null}

              <p className={`${textSize}`}>
                {highlightText(item.text, item.boldParts)}
              </p>
            </div>
          </m.div>
        ))}
      </m.div>
    </div>
  );
};
